import { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, ArrowRight, Send, ExternalLink } from "lucide-react";
import PageHero from "@/components/PageHero";
import { COMPANY } from "@/data/company";
import { CATEGORY_OPTIONS } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

// Dynamically load Google reCAPTCHA v3 script (only if a site key is configured)
function loadRecaptchaScript() {
  return new Promise((resolve) => {
    if (!RECAPTCHA_SITE_KEY) return resolve(false);
    if (window.grecaptcha && window.grecaptcha.execute) return resolve(true);
    const existing = document.querySelector(`script[data-recaptcha="v3"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-recaptcha", "v3");
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

async function getRecaptchaToken() {
  if (!RECAPTCHA_SITE_KEY) return null;
  const loaded = await loadRecaptchaScript();
  if (!loaded || !window.grecaptcha) return null;
  return new Promise((resolve) => {
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action: "contact_form_submit",
        });
        resolve(token);
      } catch {
        resolve(null);
      }
    });
  });
}

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  city: "",
  product_category: "",
  brand: "",
  message: "",
  consent: false,
};

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const preBrand = location.state?.brand;
    const preCategory = location.state?.category;
    if (preBrand || preCategory) {
      setForm((f) => ({
        ...f,
        brand: preBrand || f.brand,
        product_category: preCategory || f.product_category,
        message: f.message ||
          (preBrand ? `Please quote ${preBrand} products — grade, size and quantity to be discussed.` : ""),
      }));
    }
  }, [location.state]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.company.trim()) e.company = "Please enter your company name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.phone.trim()) e.phone = "Please enter your phone / WhatsApp";
    else if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Phone looks too short";
    if (!form.message.trim() || form.message.trim().length < 5)
      e.message = "Please describe your requirement (min. 5 characters)";
    if (!form.consent) e.consent = "Please accept the Privacy Policy to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Get reCAPTCHA v3 token (null if site key not configured)
      const recaptcha_token = await getRecaptchaToken();
      const res = await axios.post(`${API}/contact`, { ...form, recaptcha_token });
      if (res.data?.status === "success") {
        setSuccess(true);
        setForm(INITIAL);
        toast.success("Enquiry received. Our team will get back to you shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        (Array.isArray(err.response?.data?.detail) &&
          err.response.data.detail[0]?.msg) ||
        "Your enquiry didn't go through. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <PageHero
        eyebrow="Contact us"
        title="Talk to our team."
        description="Share your requirement — product, grade, size and quantity — and our team will get back to you with a quote and technical guidance."
        image="https://images.unsplash.com/photo-1552879890-3a06dd3a06c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="section bg-white">
        <div className="container-nago grid lg:grid-cols-5 gap-10">
          {/* Contact block */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-industrial">
              <div className="eyebrow">Reach us</div>
              <h3 className="mt-2 font-display font-extrabold text-2xl text-slate-900">
                Where dependable supply starts.
              </h3>
              <div className="mt-6 space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#E30613] mt-1 shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{COMPANY.address}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#E30613] mt-1 shrink-0" />
                  <div className="text-slate-700">
                    {COMPANY.phones.map((p) => (
                      <div key={p}>
                        <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-[#1E3A8A]">
                          {p}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-4 h-4 text-[#E30613] mt-1 shrink-0" />
                  <a
                    href={COMPANY.whatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-slate-700 hover:text-[#1E3A8A]"
                    data-testid="contact-whatsapp-link"
                  >
                    WhatsApp {COMPANY.whatsapp}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#E30613] mt-1 shrink-0" />
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="text-slate-700 hover:text-[#1E3A8A]"
                  >
                    {COMPANY.email}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#E30613] mt-1 shrink-0" />
                  <span className="text-slate-700">{COMPANY.hours}</span>
                </div>
              </div>
              <a
                href={COMPANY.mapsLink}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:text-[#E30613]"
              >
                Open in Google Maps <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="rounded-xl overflow-hidden border border-slate-200">
              <iframe
                title="Nago International — Google Maps"
                src={COMPANY.mapsEmbed}
                width="100%"
                height="260"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              className="card-industrial p-6 sm:p-8"
              noValidate
              data-testid="contact-form"
            >
              <div className="eyebrow">Send an enquiry</div>
              <h3 className="mt-2 font-display font-extrabold text-2xl text-slate-900">
                Request a Quote
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Include the product, grade, pack size and quantity to get the fastest response.
              </p>

              {success && (
                <div
                  className="mt-6 border border-green-200 bg-green-50 text-green-800 p-4 rounded-xl text-sm"
                  data-testid="contact-success"
                >
                  <strong>Enquiry received.</strong> Thanks for reaching out to Nago International.
                  Our team will get back to you shortly. For urgent needs, call{" "}
                  <a className="underline" href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`}>
                    {COMPANY.phones[0]}
                  </a>
                  .
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name *" error={errors.name}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="input-field"
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Company *" error={errors.company}>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    className="input-field"
                    placeholder="Company name"
                    data-testid="contact-input-company"
                  />
                </Field>
                <Field label="Email *" error={errors.email}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="input-field"
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="Phone / WhatsApp *" error={errors.phone}>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="input-field"
                    placeholder="+91 98XXXXXXXX"
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="City / State">
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    className="input-field"
                    placeholder="Belagavi, Karnataka"
                    data-testid="contact-input-city"
                  />
                </Field>
                <Field label="Product Category">
                  <select
                    value={form.product_category}
                    onChange={(e) => set("product_category", e.target.value)}
                    className="input-field bg-white"
                    data-testid="contact-select-category"
                  >
                    <option value="">Select a category</option>
                    {CATEGORY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.label}>
                        {o.label}
                      </option>
                    ))}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Brand (optional)">
                    <input
                      type="text"
                      value={form.brand}
                      onChange={(e) => set("brand", e.target.value)}
                      className="input-field"
                      placeholder="Velvex, Divyol, Kolchen…"
                      data-testid="contact-input-brand"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label="Requirement / Message *"
                    error={errors.message}
                    hint="Product, grade, pack size and quantity."
                  >
                    <textarea
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      rows={5}
                      className="input-field resize-y"
                      placeholder="Example: Please quote 210 L barrel of Velvex Synth Cut EP-25 Plus, 2 nos, delivery to Belagavi."
                      data-testid="contact-input-message"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#E30613]"
                      data-testid="contact-consent-checkbox"
                    />
                    <span>
                      I agree to the{" "}
                      <a href="/privacy-policy" className="text-[#1E3A8A] font-semibold underline">
                        Privacy Policy
                      </a>{" "}
                      and to be contacted about my enquiry.
                    </span>
                  </label>
                  {errors.consent && (
                    <div className="mt-2 text-xs text-[#E30613]" data-testid="error-consent">
                      {errors.consent}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary min-w-[180px] disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending…" : "Send Enquiry"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <Send className="w-3.5 h-3.5" /> We reply within one business day.
                </div>
              </div>

              {RECAPTCHA_SITE_KEY && (
                <div className="mt-4 text-[11px] text-slate-500 leading-relaxed" data-testid="recaptcha-notice">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#1E3A8A] underline"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#1E3A8A] underline"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .input-field {
          width: 100%;
          border: 1px solid #CBD5E1;
          border-radius: 2px;
          padding: 0.65rem 0.85rem;
          font-size: 0.9rem;
          background: #ffffff;
          color: #0F172A;
          transition: border-color 200ms, box-shadow 200ms;
        }
        .input-field:focus {
          outline: none;
          border-color: #1E3A8A;
          box-shadow: 0 0 0 3px rgba(30,58,138,0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-700 mb-1.5">
        {label}
      </label>
      {children}
      {hint && !error && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
      {error && (
        <div
          className="mt-1 text-xs text-[#E30613]"
          data-testid={`error-${label.replace(/\W/g, "-").toLowerCase()}`}
        >
          {error}
        </div>
      )}
    </div>
  );
}
